<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { mdiContentSave } from '@mdi/js'
import { useUserStore } from '@/modules/users/stores/user.store.ts'
import { useUserClientStore } from '@/modules/user-management/stores/user-client.ts'
import { useToaster } from '@/modules/toaster/composables/use-toaster'
import { languages } from '../settings/languages.ts'
import { DEFAULT_USER_SETTING_FORM } from '../constants/user.constants.ts'
import type { UserSetting } from '../models/user.model.ts'
import type {
  UserClientCarrierDto,
  UserClientCountryDto,
  UserClientDimensionDto,
  UserClientServiceGroupDto,
  UserClientViewDto,
  UserClientZipZoneDto,
} from '@/api/models/types.js'
import type { UserClientAction } from '@/modules/user-management/models/user.models.ts'

import BaseModal from '@/shared/components/modal/BaseModal.vue'
import HeaderModal from '@/shared/components/modal/HeaderModal.vue'
import BodyModal from '@/shared/components/modal/BodyModal.vue'
import FooterModal from '@/shared/components/modal/FooterModal.vue'
import PrimaryButton from '@/shared/components/button/PrimaryButton.vue'
import DefaultButton from '@/shared/components/button/DefaultButton.vue'
import TabUnderline from '@/shared/components/tab/TabUnderline.vue'
import TabContent from '@/shared/components/tab/TabContent.vue'
import SelectButton from '@/shared/components/button/SelectButton.vue'
import UserFilterSetting from './UserFilterSettingForm.vue'
import UserMotionViewSetting from './UserMotionViewSettingForm.vue'

interface Props {
  title: string
  userId?: number
}

const props = defineProps<Props>()
const open = defineModel<boolean>('open', { required: true })

const userStore = useUserStore()
const userClientStore = useUserClientStore()

const { success, error } = useToaster()

const form = ref<UserSetting>({ ...DEFAULT_USER_SETTING_FORM })
const original = ref<UserSetting>({ ...DEFAULT_USER_SETTING_FORM })
const isLoading = ref<boolean>(false)
const isSaving = ref<boolean>(false)
const tabIndex = ref<number>(0)
const carriers = ref<UserClientCarrierDto[]>([])
const countries = ref<UserClientCountryDto[]>([])
const serviceGroups = ref<UserClientServiceGroupDto[]>([])
const zipZones = ref<UserClientZipZoneDto[]>([])
const dimensions = ref<Record<number, UserClientDimensionDto[]>>({})
const actions = ref<UserClientAction[]>([])
const views = ref<UserClientViewDto[]>([])

const isDirty = computed(
  (): boolean => JSON.stringify(form.value) !== JSON.stringify(original.value),
)

const loadUserSettings = async (clientKey: string): Promise<void> => {
  const response = await userStore.getUserSettings(clientKey, props.userId)

  if (response.error || !response.data) return

  form.value = { ...response.data }
  original.value = { ...response.data }
}

const loadUserClientViews = async (clientKey: string): Promise<void> => {
  if (!userStore.user) return

  views.value = await userClientStore.getUserClientViews(clientKey, props.userId)
}

const loadUserClientActions = async (clientKey: string): Promise<void> => {
  actions.value = await userClientStore.getUserClientActions(clientKey)
}

const loadUserClientCountries = async (clientKey: string): Promise<void> => {
  countries.value = await userClientStore.getUserClientCountries(clientKey)
}

const loadUserClientCarrier = async (clientKey: string): Promise<void> => {
  carriers.value = await userClientStore.getUserClientCarriers(clientKey)
}

const loadUserClientServiceGroups = async (clientKey: string): Promise<void> => {
  serviceGroups.value = await userClientStore.getUserClientServiceGroups(clientKey)
}

const loadUserClientServiceZipZones = async (clientKey: string): Promise<void> => {
  zipZones.value = await userClientStore.getUserClientZipZones(clientKey)
}

const loadUserClientDimension = async (clientKey: string): Promise<void> => {
  dimensions.value[1] = await userClientStore.getUserClientDimension(clientKey, 1)
  dimensions.value[2] = await userClientStore.getUserClientDimension(clientKey, 2)
  dimensions.value[3] = await userClientStore.getUserClientDimension(clientKey, 3)
}

const onClose = (): void => {
  tabIndex.value = 0
  open.value = false
}

const updateUserSettings = async (): Promise<void> => {
  if (!userStore.user) return

  const response = await userStore.updateUserSettings(
    userStore.user.activeClient.clientKey,
    form.value,
    props.userId,
  )

  if (response.error) {
    form.value = { ...original.value }
    error('error.unexpected')
  } else {
    original.value = { ...form.value }
    success('message.savedSuccess')
  }
}

const onSave = async (): Promise<void> => {
  isSaving.value = true

  try {
    await updateUserSettings()
  } finally {
    isSaving.value = false
  }
}

const onLoad = async (): Promise<void> => {
  if (!userStore.user) return

  const clientKey = userStore.user.activeClient.clientKey

  try {
    loadUserClientViews(clientKey)
    loadUserClientCountries(clientKey)
    loadUserClientCarrier(clientKey)
    loadUserClientServiceGroups(clientKey)
    loadUserClientServiceZipZones(clientKey)
    loadUserClientDimension(clientKey)
    loadUserClientActions(clientKey)
    await loadUserSettings(clientKey)
  } finally {
    isLoading.value = false
  }
}

watch(
  open,
  (newVal) => {
    if (!newVal) return

    onLoad()
  },
  { immediate: true },
)
</script>

<template>
  <BaseModal :open="open" :confirm="isDirty" @close="onClose()">
    <HeaderModal :title="title" @close="onClose()" />
    <BodyModal class="min-h-82">
      <TabUnderline
        v-model="tabIndex"
        :items="[
          { label: $t('glossary.general'), disabled: isSaving },
          { label: $t('glossary.mySelection'), disabled: isSaving },
          { label: $t('glossary.motionView'), disabled: isSaving },
        ]"
      />
      <TabContent v-model="tabIndex" :index="0">
        <div class="flex gap-4">
          <SelectButton
            v-model="form.languageId"
            :title="$t('glossary.defaultLanguage')"
            :label="true"
            :items="languages"
            item-key="id"
            item-text="label"
            class="w-full"
          />
          <SelectButton
            v-model="form.viewId"
            :title="$t('glossary.defaultApplicationView')"
            :label="true"
            :items="views"
            item-key="viewId"
            item-text="viewName"
            class="w-full"
          />
        </div>
      </TabContent>
      <TabContent v-model="tabIndex" :index="1">
        <UserFilterSetting
          v-model="form"
          :user-id="userId"
          :actions="actions"
          :carriers="carriers"
          :countries="countries"
          :dimensions="dimensions"
          :service-groups="serviceGroups"
          :zip-zones="zipZones"
        />
      </TabContent>
      <TabContent v-model="tabIndex" :index="2">
        <UserMotionViewSetting v-model="form" />
      </TabContent>
    </BodyModal>
    <FooterModal class="justify-between">
      <DefaultButton :text="isDirty ? $t('common.cancel') : $t('common.close')" @click="onClose" />
      <PrimaryButton
        :text="$t('common.save')"
        class="ms-2"
        :icon="mdiContentSave"
        :loading="isSaving"
        :disabled="!isDirty"
        @click="onSave"
      />
    </FooterModal>
  </BaseModal>
</template>
